import React from "react";

import { Button, type ConfirmOptions, useConfirm } from "@/components/ui";
import type { CurriculumChangeCase } from "@/server/types";

/** Statuses a change case is withdrawn from rather than deleted: it has a review trail. */
const SUBMITTED = new Set<CurriculumChangeCase["status"]>([
  "submitted",
  "awaiting-approval",
  "changes-required",
  "approved",
]);

/** What throwing a case away costs, said in its dialog. */
function confirmationFor(status: CurriculumChangeCase["status"], label: string): ConfirmOptions {
  if (status === "team-confirmation" || status === "open") {
    return {
      title: "Discard this working revision?",
      body: `${label} and everything edited in it will be permanently deleted. This cannot be undone. The approved revision is not affected.`,
      confirmLabel: "Discard permanently",
      tone: "danger",
      "data-testid": "discard-change-case-confirmation",
    };
  }
  if (status === "approved") {
    return {
      title: "Withdraw this approved change?",
      body: `${label}'s approval is voided and the revision it approved stops being current. The revision before it becomes current again, the programme's sign-offs are kept, and a new change can be opened straight away.`,
      confirmLabel: "Withdraw approved change",
      tone: "danger",
      "data-testid": "withdraw-change-case-confirmation",
    };
  }
  return {
    title: "Withdraw this change?",
    body: `${label} stops here and can no longer be edited, reviewed or approved. Its review history is kept, the approved revision stays current, and a new change can be opened straight away.`,
    confirmLabel: "Withdraw change",
    tone: "danger",
    "data-testid": "withdraw-change-case-confirmation",
  };
}

/**
 * Throw a change case away: delete it before submission, withdraw it after.
 *
 * Renders nothing once the case is closed or the final export has locked its
 * approval, where the API accepts neither.
 */
export function DiscardChangeCase({
  changeCase,
  exportLocked = false,
  busy,
  busyReason,
  onDiscard,
  onWithdraw,
}: {
  changeCase: CurriculumChangeCase;
  /** The final export has snapshotted this change's approved revision. */
  exportLocked?: boolean;
  /** Another write is running. */
  busy: boolean;
  busyReason: string;
  onDiscard: () => void;
  onWithdraw: () => void;
}): React.JSX.Element | null {
  const confirm = useConfirm();
  const label = changeCase.institutionalLabel ?? "This change";
  const unsent = changeCase.status === "team-confirmation" || changeCase.status === "open";
  const approved = changeCase.status === "approved";
  if ((!unsent && !SUBMITTED.has(changeCase.status)) || (approved && exportLocked)) {
    return null;
  }

  const ask = (): void => {
    confirm(confirmationFor(changeCase.status, label)).then((confirmed) => {
      if (confirmed) {
        (unsent ? onDiscard : onWithdraw)();
      }
    });
  };

  return (
    <Button
      variant="outline"
      tone="danger"
      size="sm"
      icon={unsent ? "trash" : "x-circle"}
      onClick={ask}
      disabled={busy}
      disabledReason={busyReason}
      data-testid={unsent ? "discard-change-case" : "withdraw-change-case"}
    >
      {unsent ? "Discard working revision" : "Withdraw change"}
    </Button>
  );
}
